import { ALERTS_ASYNC_STATUS } from "./constants";
import { getResizingFormatItemPatch } from "./buildResizeImagePayload";
import {
  CREATIVE_ALERT_PLACEHOLDER_KIND,
  CREATIVE_ALERT_TYPE,
  getCreativeJobAlertConfig,
} from "./creativeJobAlertConfig";

const buildJobPlaceholderBase = ({ alert, suffix }) => {
  const jobID = alert?.jobID ?? alert?.id;
  const placeholderId = `creative-job-${jobID}-${suffix}`;
  const now = Date.now();

  return {
    id: placeholderId,
    creativeJobAlertId: jobID,
    url: placeholderId,
    creationTime: now,
    creationTimeNegative: -now,
  };
};

export const isCreativeJobAlertPlaceholder = (item) =>
  Boolean(item?.creativeJobAlertId);

const BATCH_RESIZE_PLACEHOLDER_PREFIX = "batch-resize-placeholder";

const isBatchResizePlaceholderItem = (item) =>
  typeof item?.id === "string" &&
  (item.id === BATCH_RESIZE_PLACEHOLDER_PREFIX ||
    item.id.startsWith(`${BATCH_RESIZE_PLACEHOLDER_PREFIX}-`));

const buildPlaceholdersForAlert = (alert) => {
  const { placeholderKind } = getCreativeJobAlertConfig(alert?.jobType);
  const generationData = alert?.generationData || {};

  switch (placeholderKind) {
    case CREATIVE_ALERT_PLACEHOLDER_KIND.resize: {
      const formats = Array.isArray(generationData.formats)
        ? generationData.formats
        : [];

      if (formats.length === 0) {
        return [
          {
            ...buildJobPlaceholderBase({ alert, suffix: "resize" }),
            ...getResizingFormatItemPatch({
              format: "square",
              width: generationData.width,
              height: generationData.height,
            }),
          },
        ];
      }

      return formats.map((format) => ({
        ...buildJobPlaceholderBase({ alert, suffix: format }),
        ...getResizingFormatItemPatch({
          format,
          width: generationData.width,
          height: generationData.height,
        }),
      }));
    }
    case CREATIVE_ALERT_PLACEHOLDER_KIND.edit:
      return [
        {
          ...buildJobPlaceholderBase({ alert, suffix: "edit" }),
          resizingFormat: "edit",
        },
      ];
    case CREATIVE_ALERT_PLACEHOLDER_KIND.animate:
      return [
        {
          ...buildJobPlaceholderBase({ alert, suffix: "animate" }),
          resizingFormat: "animate",
        },
      ];
    case CREATIVE_ALERT_PLACEHOLDER_KIND.batch: {
      const items = Array.isArray(generationData.items)
        ? generationData.items
        : [];

      if (items.length === 0) {
        return [
          {
            ...buildJobPlaceholderBase({ alert, suffix: "batch" }),
            resizingFormat: "edit",
          },
        ];
      }

      return items.map((item, index) => ({
        ...buildJobPlaceholderBase({
          alert,
          suffix: item?.selectionId || String(index),
        }),
        resizingFormat: "edit",
      }));
    }
    case CREATIVE_ALERT_PLACEHOLDER_KIND.none:
      return [];
    default: {
      const exhaustiveCheck = placeholderKind;
      void exhaustiveCheck;
      return [];
    }
  }
};

export const getCreativeJobAlertPlaceholders = ({
  notifications = [],
} = {}) => {
  const loadingAlerts = notifications.filter(
    (alert) =>
      alert?.type === CREATIVE_ALERT_TYPE.generatingCreative &&
      alert?.status === ALERTS_ASYNC_STATUS.LOADING &&
      getCreativeJobAlertConfig(alert?.jobType).placeholderKind !==
        CREATIVE_ALERT_PLACEHOLDER_KIND.none
  );

  return loadingAlerts.flatMap(buildPlaceholdersForAlert);
};

export const mergeAlertPlaceholdersWithData = ({
  dataItems = [],
  notifications = [],
} = {}) => {
  const placeholders = getCreativeJobAlertPlaceholders({ notifications });

  const withoutPlaceholders = dataItems.filter(
    (item) =>
      !isCreativeJobAlertPlaceholder(item) &&
      !isBatchResizePlaceholderItem(item) &&
      item?.resizingFormat !== "animate" &&
      item?.resizingFormat !== "edit"
  );

  return [...placeholders, ...withoutPlaceholders];
};
